import type { MatterType } from '../enums';
import type { TemplateFormLanguage } from '../types/templateForms';
import { getIntakeFormTitle, getMatterTypeLabel } from '../util';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from './constants';

/** Canadian French labels for matter types; English labels live in MATTER_TYPE_LABELS. */
export const FRENCH_MATTER_TYPE_LABELS: Record<MatterType, string> = {
    Sale: 'Vente',
    Purchase: 'Achat',
    Refinance: 'Refinancement',
    PrivateLender: 'Prêteur privé',
    TitleTransfer: 'Transfert de titre',
    BareTransfer: 'Transfert simple',
    BareTransferAndRefinance: 'Transfert simple et refinancement',
    ILA: 'Avis juridique indépendant',
    PurchaseAndSale: 'Achat et vente',
    Transfer: 'Transfert',
    TransferAndRefinance: 'Transfert et refinancement',
    TransferOfTitle: 'Transfert de titre de propriété',
    SurvivorshipApplication: 'Demande de survivance',
    ForProfitIncorporation: 'Incorporation à but lucratif',
    NotForProfitIncorporation: 'Incorporation à but non lucratif',
    Corporate: 'Société',
    EstatePlanning: 'Planification successorale',
    EstateAdministration: 'Administration successorale',
    PowerOfAttorney: 'Procuration',
    Civil: 'Droit civil',
    PersonalInjury: 'Préjudice corporel',
    EmploymentLaw: 'Droit du travail',
    LandlordTenant: 'Propriétaire et locataire',
    MotorVehicleAccident: 'Accident de véhicule automobile',
    Litigation: 'Litige',
    Family: 'Droit de la famille',
    Divorce: 'Divorce',
    CohabitationAgreement: 'Contrat de vie commune',
    SeparationAgreement: 'Entente de séparation',
    ParentingAgreement: 'Entente parentale',
    SpousalSupport: 'Pension alimentaire pour conjoint',
    MarriageCohabitation: 'Mariage/vie commune',
    FertilityLaw: 'Droit de la fertilité',
    MotionToChange: 'Motion en modification',
    SeparationAndDivorce: 'Séparation et divorce',
    IndependentLegalAdvice: 'Avis juridique indépendant',
    Criminal: 'Droit criminel',
    ImpairedDriving: 'Conduite avec facultés affaiblies',
    Assault: 'Voies de fait',
    TheftAndFraud: 'Vol et fraude',
    Immigration: 'Immigration',
    TemporaryStatus: 'Statut temporaire',
    PermanentResidence: 'Résidence permanente',
    Citizenship: 'Citoyenneté',
    GeneralIntake: 'Admission générale',
    StandAloneIDV: 'Vérification d’identité autonome',
};

const FRENCH_INTAKE_FORM_TITLE = 'Formulaire d’admission';

/**
 * Localized version of getIntakeFormTitle. English keeps the existing
 * "{Matter Type} Intake Form" shape; French renders
 * "Formulaire d’admission – {type de dossier}".
 */
export function getLocalizedIntakeFormTitle(
    matterType?: string | null,
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
): string {
    if (language !== 'fr') {
        return getIntakeFormTitle(matterType);
    }

    if (!matterType) {
        return FRENCH_INTAKE_FORM_TITLE;
    }

    // "Formulaire d’admission – Admission générale" would be redundant
    if (matterType === 'GeneralIntake') {
        return 'Formulaire d’admission générale';
    }

    const label =
        FRENCH_MATTER_TYPE_LABELS[matterType as MatterType] ?? getMatterTypeLabel(matterType);
    return `${FRENCH_INTAKE_FORM_TITLE} – ${label}`;
}
